// TODO: move into local styles
import 'pages/contacts/contact-details/notes-tab/note/index.css'

import * as React from 'react'

import {
  cancelTrackComment,
  deleteTrackComment,
  editTrackCommentFinish,
  editTrackCommentStart,
  updateEditedTrack,
} from '@disco/state-management/actions/ServerActions'
import dayjs from '@disco/utils/dayjs'

import type { Comment, EditableTrack, TrackId } from 'types/track'

import { Flex, Text } from 'components/ds'
import EditMetadataComment from 'components/track-actions/edit-metadata/comment'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import Icon from 'components/ui/icon'
import Tooltip from 'components/ui/tooltip'

type Props = {
  editedTrack: EditableTrack & {
    trackIds?: TrackId[]
  }
  saving: boolean
  saveTrackComment: (index: number) => unknown
  editTrackCommentStart?: typeof editTrackCommentStart
  editTrackCommentFinish?: typeof editTrackCommentFinish
  cancelTrackComment: typeof cancelTrackComment
  deleteTrackComment?: typeof deleteTrackComment
  updateEditedTrack: typeof updateEditedTrack
}

const isInEditMode = comments => {
  return (
    comments && comments.some(comment => comment.isEditing && !comment.isNew)
  )
}

const isEditorOpen = comments => {
  return (
    comments && comments.some(comment => comment.isEditing || comment.isNew)
  )
}

export default class TrackComments extends React.Component<Props> {
  componentDidMount() {
    if (!isEditorOpen(this.props.editedTrack.comments)) this._handleCommentAdd()
  }

  componentDidUpdate() {
    if (!isEditorOpen(this.props.editedTrack.comments)) this._handleCommentAdd()
  }

  _convertBreakCharsToParagraphs(
    text: string,
  ): Array<React.ReactElement<React.ComponentProps<'p'>, 'p'>> {
    return text
      .split('\n')
      .map<React.ReactElement<React.ComponentProps<'p'>, 'p'>>(
        (paragraph, i) => (
          <Text size="2" key={i}>
            {paragraph || <br />}
          </Text>
        ),
      )
  }

  _handleCommentEditStart(index: number) {
    if (!this.props.editTrackCommentStart) return
    this.props.editTrackCommentStart(index)
  }

  _handleCommentEditFinish(index: number) {
    if (!this.props.editTrackCommentFinish) return
    this.props.editTrackCommentFinish(index)
  }

  _handleCommentCancel(index: number) {
    this.props.cancelTrackComment(index)
  }

  _handleCommentDelete(index: number) {
    if (!this.props.deleteTrackComment) return
    this.props.deleteTrackComment(index)
  }

  _handleCommentSave(index: number, evt: React.SyntheticEvent<any>) {
    evt.preventDefault()
    this.props.saveTrackComment(index)
  }

  // TODO: move updateEditedTrack in _handleCommentChange to reducers
  _handleCommentChange(
    index: number,
    evt: React.ChangeEvent<HTMLTextAreaElement>,
  ) {
    const editedTrack = this.props.editedTrack
    const comments = editedTrack.comments
    if (!comments || index > comments.length - 1) return
    const comment = comments[index]
    this.props.updateEditedTrack({
      ...editedTrack,
      comments: [
        ...comments.slice(0, index),
        { ...comment, comment: evt.target.value },
        ...comments.slice(index + 1),
      ],
    })
  }

  _handleVisibilityChange(index: number, value: 'client' | 'internal') {
    const editedTrack = this.props.editedTrack
    const comments = editedTrack.comments
    if (!comments || index > comments.length - 1) return
    const comment = comments[index]
    this.props.updateEditedTrack({
      ...editedTrack,
      comments: [
        ...comments.slice(0, index),
        { ...comment, visibility: value },
        ...comments.slice(index + 1),
      ],
    })
  }

  _handleCommentAdd() {
    const editedTrack = this.props.editedTrack
    this.props.updateEditedTrack({
      ...editedTrack,
      comments: [
        ...(editedTrack.comments || []).map(c => ({ ...c, isEditing: false })),
        {
          comment: '',
          isNew: true,
          isEditing: true,
          visibility: 'internal',
        },
      ],
    })
  }

  _renderCommentAuthor(comment: Partial<Comment>): null | string {
    const { user } = comment
    const author = user ? user.first_name : null
    return author
  }

  _renderCommentEdit(comment: Partial<Comment>): React.ReactElement<'span'> {
    const { user, timestamp, last_modified_by, last_modified_on } = comment

    if (last_modified_by) {
      const editDate = dayjs(last_modified_on).format('MMMM D, YYYY')
      const editTime = dayjs(last_modified_on).format('LLLL')
      return last_modified_by.id === user?.id ? (
        <Text as="span" color="gray" title={editTime}>
          edited on {editDate}
        </Text>
      ) : (
        <Text as="span" color="gray" title={editTime}>
          edited by {last_modified_by.first_name} on {editDate}
        </Text>
      )
    } else {
      const editDate = dayjs(timestamp).format('MMMM D, YYYY')
      const editTime = dayjs(timestamp).format('LLLL')
      return (
        <Text as="span" color="gray" title={editTime}>
          {editDate}
        </Text>
      )
    }
  }

  _renderExistingComment(
    comment: Partial<Comment>,
    index: number,
  ): React.ReactElement<'div'> {
    const { deleteTrackComment, editTrackCommentStart, saving } = this.props
    return (
      <div className="contact-note" key={index}>
        <div className="contact-note__info">
          <div className="contact-note__info__actions">
            <Tooltip
              content={
                comment.visibility === 'internal'
                  ? 'Internal note'
                  : 'Clients can see this note'
              }
            >
              <Icon
                color={
                  comment.visibility === 'internal' ? '#fc644d' : '#32b57c'
                }
                type="watch"
              />
            </Tooltip>
            <EllipsisMenu
              compact
              hasArrow={false}
              icon="ellipsis"
              options={[
                {
                  disabled: saving || !editTrackCommentStart,
                  label: 'Edit',
                  onClick: () => this._handleCommentEditStart(index),
                },
                {
                  destructive: true,
                  disabled: saving || !deleteTrackComment,
                  label: 'Delete',
                  onClick: () => this._handleCommentDelete(index),
                },
              ]}
            />
          </div>
        </div>
        <Flex
          direction="column"
          maxHeight="400px"
          width="100%"
          overflowY="hidden"
          gap="4"
          className="contact-note__content"
        >
          <Flex
            mb="6"
            maxHeight="300px"
            overflow="auto"
            direction="column"
            gap="2"
          >
            {this._convertBreakCharsToParagraphs(comment.comment ?? '')}
          </Flex>
          <Flex className="contact-note__date">
            {this._renderCommentEdit(comment)}
          </Flex>
        </Flex>
        <div className="contact-note__author">
          <Text as="span" color="gray">
            {this._renderCommentAuthor(comment)}
          </Text>
        </div>
      </div>
    )
  }

  _renderComments() {
    const comments = this.props.editedTrack.comments
    const editMode = isInEditMode(this.props.editedTrack.comments)
    if (!comments) return null
    return comments.map((comment, index) => {
      if (comment.isEditing) {
        return (
          <EditMetadataComment
            key={index}
            comment={comment}
            trackId={this.props.editedTrack.id}
            trackIds={this.props.editedTrack.trackIds}
            onVisibilityChange={value =>
              this._handleVisibilityChange(index, value)
            }
            onCommentChange={e => this._handleCommentChange(index, e)}
            onCommentSave={
              comment.isNew
                ? this._handleCommentSave.bind(this, index)
                : this._handleCommentEditFinish.bind(this, index)
            }
            onCommentCancel={this._handleCommentCancel.bind(this, index)}
            saving={this.props.saving}
          />
        )
      } else {
        if (!editMode) {
          return this._renderExistingComment(comment, index)
        }
      }
    })
  }

  render() {
    return this._renderComments()
  }
}
